import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { useAIDTMediaQuery } from "../../hooks";
import { Stack, Input, Typography, useTheme, Theme, Box } from "@mui/material";
import { Micro, ReadyRecord, Reset, SendArrow } from "../../icons";
import { EliceIconButton } from "../icon-button";
import { RecorderProvider, useRecorder } from "../recorder-step/RecorderContext";

import type { RecorderType } from "../recorder-step/RecorderStep";
import AIAvatar from '../../assets/AIAvatar.png';
import AssistantAvatar from "./AssistantAvatar";
import UserAvatar from "./UserAvatar";
import TypingIndicator from "./TypingIndicator";
import { EliceRecorderStep } from "../recorder-step";

export type EliceMessageItem = { role: "system" | "user" | "assistant"; content: string }

export interface EliceChatProps {
  placeholder?: string;
  messages: EliceMessageItem[];
  content: string;
  onReset: () => void;
  onChange: (text: string) => void;
  onSend: (text: string) => void;
  onRecord?: () => void;
  setAudioFile?: (file: File | null) => void;
  onTransform?: () => void;
  height?: string;
  onTranscribingChange?: (value: boolean) => void;
  isLoadingMessage?: boolean;
  isDisabled?: boolean;
  backgroundImage?: string;
  CustomAssistantAvatar?: React.ReactNode;
  CustomUserAvatar?: React.ReactNode;
  type?: string;
  recorderType?: RecorderType;
  isOnlyRecord: boolean;
  isRecordDisabled?: boolean;
  isResetDisabled?: boolean;
  maxRows?: number;
}

const EliceChat = (props: React.ComponentProps<typeof EliceChatContainer>) => {
  return (
    <RecorderProvider>
      <EliceChatContainer {...props} />
    </RecorderProvider>
  );
};

const EliceChatContainer = ({
  placeholder = "AI와 대화를 시작해 보세요.",
  messages = [],
  content = "",
  onReset,
  onSend,
  onChange,
  onRecord,
  onTranscribingChange,
  setAudioFile,
  onTransform,
  isLoadingMessage,
  CustomAssistantAvatar,
  CustomUserAvatar,
  isDisabled = false,
  backgroundImage,
  height = "100%",
  recorderType = 'notNeed',
  isOnlyRecord = false,
  isResetDisabled = false,
  isRecordDisabled = false,
  maxRows = 8,
}: EliceChatProps) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(content);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { state: recorderState, setIsRecording, resetAnalyzer } = useRecorder();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoadingMessage]);
  useEffect(() => {
    if (recorderState.step === "ready") {
      resetAnalyzer();
    }
  }, [recorderState.step])

  const onClickRecordSubmit = () => {
    onTransform?.();
  }

  const onClickCloseIsRecording = () => {
    onTranscribingChange?.(false);
    setIsRecording(false);
  }

  const handleSend = () => {
    if (inputValue.trim()) {
      onSend(inputValue);
      setInputValue("");
    }
  };

  return (
    <StyledChatContainer
      theme={theme}
      height={height}
      backgroundImage={backgroundImage}
    >
      {
        (recorderState.isRecording && setAudioFile) && (
          <RecordingOverlay onClick={onClickCloseIsRecording}>
            <OverlayContent onClick={(e) => e.stopPropagation()}>
              <EliceRecorderStep
                closeRecorder={onClickCloseIsRecording}
                onAudioReady={setAudioFile}
                onRecord={onRecord}
                onTransform={onTransform}
                type={recorderType}
                visualType="bar"
                onTranscribingChange={onTranscribingChange}
                isLoadingMessage={isLoadingMessage}
              />
            </OverlayContent>
          </RecordingOverlay>
        )
      }
      <EliceChat.MessageList
        messages={messages}
        isLoadingMessage={isLoadingMessage}
        scrollRef={scrollRef}
        CustomAssistantAvatar={CustomAssistantAvatar}
        CustomUserAvatar={CustomUserAvatar}
      />
      <EliceChat.InputArea
        placeHolder={placeholder}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e.target.value);
        }}
        isOnlyRecord={isOnlyRecord}
        setAudioFile={setAudioFile}
        onTransform={onClickRecordSubmit}
        onSend={handleSend}
        onReset={onReset}
        onRecord={onRecord}
        onTranscribingChange={onTranscribingChange}
        isDisabled={isDisabled}
        type={recorderType}
        isResetDisabled={isResetDisabled}
        isRecordDisabled={isRecordDisabled}
        maxRows={maxRows}
      />
    </StyledChatContainer>
  );
};

// ✅ 메시지 리스트 컴포넌트
EliceChat.MessageList = ({
  messages,
  isLoadingMessage,
  scrollRef,
  height = "100%",
  CustomAssistantAvatar,
  CustomUserAvatar,
}: {
  messages: { role: "system" | "user" | "assistant"; content: string }[];
  isLoadingMessage?: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  height?: string;
  CustomAssistantAvatar: React.ReactNode;
  CustomUserAvatar: React.ReactNode;
}) => {
  const theme = useTheme();
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isAssistantLoading = !lastMessage || lastMessage.role === 'user';

  return (
    <StyledScrollBarStack theme={theme} height={height}>
      {messages.length === 0 && !isLoadingMessage ? (
        <EmptyMessageContainer>
          <AIAvatarImage src={AIAvatar} alt="Ai-avatar" />
          <Typography color={theme.palette.text.disabled} variant="subtitle1">
            AI와 대화를 시작해 보세요.
          </Typography>
        </EmptyMessageContainer>
      ) : (
        <>
          {messages.map((msg, index) => {
            if (msg.role === "system") return null;
            const isAssistant = msg.role === "assistant";

            return (
              <Stack
                key={index}
                direction={isAssistant ? "row" : "row-reverse"}
                gap="0.5rem"
                alignItems="center"
              >
                <Stack flexShrink={0}>
                  {isAssistant ? (CustomAssistantAvatar ?? <AssistantAvatar />) : (CustomUserAvatar ?? <UserAvatar />)}
                </Stack>
                <StyledMessage isAssistant={isAssistant} theme={theme}>
                  {msg.content}
                </StyledMessage>
              </Stack>
            );
          })}
          {isLoadingMessage &&
            (isAssistantLoading ? (
              <Stack direction="row" gap="0.5rem" alignItems="center">
                <Stack flexShrink={0}>
                  {CustomAssistantAvatar ?? <AssistantAvatar />}
                </Stack>
                <TypingIndicator />
              </Stack>
            ) : (
              <Stack direction="row-reverse" gap="0.5rem" alignItems="center">
                <Stack flexShrink={0}>
                  {CustomUserAvatar ?? <UserAvatar />}
                </Stack>
                <TypingIndicator />
              </Stack>
            ))}
          <div ref={scrollRef} />
        </>
      )}
    </StyledScrollBarStack>
  );
};

EliceChat.InputArea = ({
  placeHolder,
  value,
  isDisabled,
  onChange,
  onSend,
  onReset,
  onRecord,
  onTranscribingChange,
  isOnlyRecord,
  isResetDisabled,
  isRecordDisabled,
  maxRows
}: {
  placeHolder: string,
  value: string;
  isDisabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSend: () => void;
  onReset: () => void;
  onRecord?: () => void;
  onTransform?: () => void;
  setAudioFile?: (file: File) => void;
  onTranscribingChange?: (value: boolean) => void;
  type: RecorderType;
  isOnlyRecord: boolean;
  isResetDisabled: boolean;
  isRecordDisabled: boolean;
  maxRows: number;
}) => {
  const theme = useTheme();
  const { state: recorderState, setIsRecording } = useRecorder();
  const { isTablet, isMiniTablet } = useAIDTMediaQuery();
  return (
    <Stack
      direction={isMiniTablet ? "column" : "row"}
      display={recorderState.isRecording ? "none" : ""}
      bgcolor={theme.palette.grey[50]}
      borderRadius="1.125rem"
      height={'auto'}
      width={recorderState.isRecording ? "100%" : "auto"}
      justifyContent={recorderState.isRecording ? "center" : "none"}
      alignItems={recorderState.isRecording ? "center" : "none"}
      p="0.5rem 1rem"
      gap="0.625rem"
      zIndex="1000"
    >
      {
        isOnlyRecord ? (
          <OnlyRecordButton
            theme={theme}
            onClick={() => {
              onRecord?.();
              setIsRecording(true);
              onTranscribingChange?.(true);
            }}>
            <ReadyRecord />
            <Typography variant="h3">녹음 시작</Typography>
          </OnlyRecordButton>
        ) : (
          <>
            <Stack flexGrow={1} maxHeight="14.625rem" height="100%">
              <StyledInput
                isTablet={isTablet}
                disabled={isDisabled}
                value={value}
                onChange={onChange}
                placeholder={placeHolder}
                theme={theme}
                multiline
                maxRows={maxRows}
                onKeyUpCapture={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (value.trim()) {
                      onSend();
                    }
                  }
                }}
              />
            </Stack>
            <Stack
              direction={"row"}
              display="flex"
              justifyContent={isMiniTablet ? 'flex-end' : 'center'}
              gap={'0.625rem'}
              height={isTablet ? '3rem' : '3.5rem'}
            >
              {onRecord && (
                <EliceIconButton
                  width={isTablet ? '3rem' : '3.5rem'}
                  height={isTablet ? '3rem' : '3.5rem'}
                  onClick={() => {
                    onRecord();
                    setIsRecording(true);
                    onTranscribingChange?.(true);
                  }}
                  disabled={isRecordDisabled}
                >
                  <Micro color={theme.palette.secondary.main} />
                </EliceIconButton>
              )}
              <EliceIconButton
                onClick={onReset}
                width={isTablet ? '3rem' : '3.5rem'}
                height={isTablet ? '3rem' : '3.5rem'}
                disabled={isResetDisabled}>
                <Reset color={theme.palette.secondary.main}
                />
              </EliceIconButton>
              <EliceIconButton
                onClick={onSend} width={isTablet ? '3rem' : '3.5rem'}
                height={isTablet ? '3rem' : '3.5rem'}
                disabled={!value.trim()}
              >
                <SendArrow color={theme.palette.secondary.main} />
              </EliceIconButton>
            </Stack>
          </>
        )
      }
    </Stack>
  );
};

const StyledChatContainer = styled(Stack) <{ theme: Theme; height: string; backgroundImage?: string; }>`
  position: relative;
  max-height: 100%;
  height: ${({ height }) => height ?? 'fit-content'};
  max-width: 100%;
  border: 2px solid ${({ theme }) => theme.palette.grey[300]};
  border-radius: 1.5rem;
  padding: 1.5rem;
  gap: 1.75rem;

  ${({ backgroundImage }) =>
    backgroundImage &&
    `
    background-image: url(${backgroundImage});
    background-size: cover;
    background-position: center;
  `}
`;
const StyledScrollBarStack = styled(Stack) <{ theme: Theme, height: string }>`
  flex: 1;
  overflow-y: auto;
  border-radius: 1.125rem;
  padding-right: 1rem;
  ::-webkit-scrollbar {
    width: 0.25rem;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.grey[200]};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.primary.main};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const StyledInput = styled(Input) <{ theme: Theme, isTablet: boolean }>`
  width: 100%;
  height: 100%;
  background-color: transparent;
  font-size: ${({ isTablet }) => isTablet ? '0.5rem' : '1.2rem'} ;
  font-weight: ${({ isTablet }) => isTablet ? 500 : 700};
  outline: none;
  resize: none;
  overflow-y: auto;
  color: ${({ theme }) => theme.palette.secondary.main};
  ::placeholder {
    color: ${({ theme }) => theme.palette.grey[300]};
  }
  &:has(> textarea:disabled)::before {
    border: none;
  }
  
  ::before {
    border: none;
  }
  :hover {
    ::before {
      border: none;
    }
  }
  ::after {
    border-bottom: none !important;
  }
  
   ::-webkit-scrollbar {
    width: 0.25rem;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.grey[200]};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.primary.main};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const StyledMessage = styled(Typography) <{ isAssistant: boolean, theme: Theme }>`
  color: ${({ isAssistant, theme }) =>
    isAssistant ? theme.palette.text.secondary : theme.palette.secondary.main};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  font-weight: ${({ isAssistant }) => (isAssistant ? 500 : 700)};
  background-color: ${({ isAssistant, theme }) =>
    isAssistant ? theme.palette.grey[50] : theme.palette.primary.light};
  border-radius: 1rem;
  white-space: pre-wrap;
  padding: 0.75rem;
  margin-bottom: 16px;
`;

const EmptyMessageContainer = styled(Stack)`
  display:flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  gap: 1rem;
`

const AIAvatarImage = styled.img`
  width: 10rem;
  height: 10rem;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
`

const RecordingOverlay = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  border-radius: 1.5rem;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
`;

const OverlayContent = styled(Box)`
  width: 100%;
  height: 212px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 1.5rem;
`;

const OnlyRecordButton = styled(Stack) <{ theme: Theme }>`
  color: ${({ theme }) => theme.palette.common.white};
  background:${({ theme }) => theme.palette.common.red[900]};
  width: 100%;
  flex-direction: row;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  height: 55px;
  gap: 8px;

  &:hover {
    background: ${({ theme }) => theme.palette.common.red[600]};
    color: ${({ theme }) => theme.palette.common.red[900]};
  }
`

export default EliceChat;
